import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../services/supabase";
import AddressAutocompletePlaces from "../AddressAutocompletePlaces";
import { useNavigate } from "react-router-dom";

const RideForm = ({ mode = "create", initialData = null, rideId = null }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nom: "",
    telephone: "",
    email: "",
    client_id: null,
    driver_id: null,
    depart: "",
    depart_lat: null,
    depart_lng: null,
    destination: "",
    destination_lat: null,
    destination_lng: null,
    extra_info: "",
    type_reservation: "immediate",
  });

  const [assignMode, setAssignMode] = useState("auto");
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [driversOnline, setDriversOnline] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clientSearch, setClientSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [dateHeure, setDateHeure] = useState("");

  const isLocked = initialData?.status === "completed";

  const hasInitializedRef = useRef(false);

  const draftKey =
    mode === "edit" && rideId
      ? `ride_form_draft_${rideId}`
      : "ride_form_draft_create";

  const formatDateTimeLocal = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const normalizePhone = (phone) => {
    if (!phone) return null;

    const digits = phone.replace(/\D/g, "");
    if (!digits) return null;

    const digits10 =
      digits.length === 11 && digits.startsWith("1")
        ? digits.slice(1)
        : digits.length === 10
        ? digits
        : null;

    const digits11 =
      digits.length === 10
        ? "1" + digits
        : digits.length === 11
        ? digits
        : null;

    return { digits, digits10, digits11 };
  };

  const loadOnlineDrivers = async (driverIdToInclude = null) => {
    try {
      let query = supabase
        .from("drivers")
        .select("id, num_driver, status")
        .eq("approved", true)
        .eq("is_active", true);

      if (driverIdToInclude) {
        query = query.or(`status.eq.online,id.eq.${driverIdToInclude}`);
      } else {
        query = query.eq("status", "online");
      }

      const { data, error } = await query;

      if (error) {
        console.error("Erreur chargement chauffeurs:", error);
        setDriversOnline([]);
        return;
      }

      setDriversOnline(data || []);
    } catch (e) {
      console.error("Erreur chargement chauffeurs:", e);
      setDriversOnline([]);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssignModeChange = (modeValue) => {
    setAssignMode(modeValue);

    if (modeValue === "manual") {
      loadOnlineDrivers(selectedDriverId || initialData?.driver_id || null);
      return;
    }

    if (modeValue === "auto") {
      setSelectedDriverId(null);
    }

    if (modeValue === "unassigned") {
      setSelectedDriverId(null);
    }
  };

  useEffect(() => {
    if (hasInitializedRef.current) return;

    let baseFormData = {
      nom: "",
      telephone: "",
      email: "",
      client_id: null,
      driver_id: null,
      depart: "",
      depart_lat: null,
      depart_lng: null,
      destination: "",
      destination_lat: null,
      destination_lng: null,
      extra_info: "",
      type_reservation: "immediate",
    };

    let baseAssignMode = "auto";
    let baseSelectedDriverId = null;
    let baseDateHeure = "";

    if (initialData) {
      baseFormData = {
        nom: initialData.clients?.full_name || "",
        telephone: initialData.clients?.phone || "",
        email: initialData.clients?.email || "",
        client_id: initialData.client_id || null,
        driver_id: initialData.driver_id || null,
        depart: initialData.pickup_address || "",
        depart_lat: initialData.pickup_lat || null,
        depart_lng: initialData.pickup_lng || null,
        destination: initialData.dropoff_address || "",
        destination_lat: initialData.dropoff_lat || null,
        destination_lng: initialData.dropoff_lng || null,
        extra_info: initialData.extra_info || "",
        type_reservation: initialData.date_heure ? "planifiee" : "immediate",
      };

      baseDateHeure = formatDateTimeLocal(initialData.date_heure);

      if (initialData.driver_id) {
        baseAssignMode = "manual";
        baseSelectedDriverId = initialData.driver_id;
      }
    }

    const savedDraft = sessionStorage.getItem(draftKey);

    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);

        if (parsed.formData) baseFormData = parsed.formData;
        if (parsed.assignMode) baseAssignMode = parsed.assignMode;
        if (parsed.selectedDriverId !== undefined) {
          baseSelectedDriverId = parsed.selectedDriverId;
        }
        if (parsed.dateHeure !== undefined) {
          baseDateHeure = parsed.dateHeure;
        }
      } catch (e) {
        console.error("Erreur lecture brouillon formulaire:", e);
      }
    }

    setFormData(baseFormData);
    setAssignMode(baseAssignMode);
    setSelectedDriverId(baseSelectedDriverId);
    setDateHeure(baseDateHeure);

    if (baseAssignMode === "manual") {
      loadOnlineDrivers(baseSelectedDriverId || initialData?.driver_id || null);
    }

    hasInitializedRef.current = true;
  }, [initialData, draftKey]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;

    const draft = {
      formData,
      assignMode,
      selectedDriverId,
      dateHeure,
    };

    sessionStorage.setItem(draftKey, JSON.stringify(draft));
  }, [formData, assignMode, selectedDriverId, dateHeure, draftKey]);

  const handleSearchClient = async () => {
    if (!clientSearch.trim()) return;

    setSearchLoading(true);

    try {
      const search = clientSearch.trim();
      const emailSearch = search.includes("@") ? search.toLowerCase() : null;
      const phone = normalizePhone(search);

      let query = supabase
        .from("profiles")
        .select("id, full_name, phone, email");

      if (emailSearch) {
        query = query.ilike("email", `%${emailSearch}%`);
      } else if (phone) {
        const conditions = [];

        if (phone.digits) conditions.push(`phone.ilike.%${phone.digits}%`);
        if (phone.digits10) conditions.push(`phone.ilike.%${phone.digits10}%`);
        if (phone.digits11) conditions.push(`phone.ilike.%${phone.digits11}%`);

        if (conditions.length === 0) {
          alert("Numéro invalide");
          setSearchLoading(false);
          return;
        }

        query = query.or(conditions.join(","));
      } else {
        alert("Veuillez entrer un téléphone ou un email valide.");
        setSearchLoading(false);
        return;
      }

      const { data, error } = await query.limit(1);

      if (error) throw error;

      if (data?.length) {
        const client = data[0];
        setFormData((prev) => ({
          ...prev,
          nom: client.full_name || "",
          telephone: client.phone || "",
          email: client.email || "",
          client_id: client.id,
        }));
        alert("Client trouvé et pré-rempli.");
      } else {
        alert("Aucun client trouvé.");
      }
    } catch (e) {
      console.error("❌ SUPABASE SEARCH ERROR:", e);
      alert("Erreur lors de la recherche du client.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.depart || !formData.destination) {
      alert("Adresse de départ et de destination obligatoires.");
      return;
    }

    if (
      formData.depart_lat == null ||
      formData.depart_lng == null ||
      formData.destination_lat == null ||
      formData.destination_lng == null
    ) {
      alert("Veuillez sélectionner des adresses valides.");
      return;
    }

    if (formData.type_reservation === "planifiee" && !dateHeure) {
      alert("Veuillez sélectionner une date et une heure.");
      return;
    }

    if (assignMode === "manual" && !selectedDriverId) {
      alert("Veuillez choisir un chauffeur.");
      return;
    }

    if (mode === "edit" && initialData?.status === "completed") {
      alert("Cette course est complétée et ne peut plus être modifiée.");
      return;
    }

    setIsSubmitting(true);

    try {
      let status = initialData?.status || "requested";
      let driver_id = initialData?.driver_id || null;
      let accepted_at = initialData?.accepted_at || null;

      if (mode === "create") {
        status = "requested";
        driver_id = null;
        accepted_at = null;

        if (assignMode === "unassigned") {
          status = "unassigned";
        }

        if (assignMode === "manual") {
          driver_id = selectedDriverId;
          status = "accepted";
          accepted_at = new Date().toISOString();
        }
      }

      if (mode === "edit") {
        if (assignMode === "unassigned") {
          driver_id = null;
          status = "unassigned";
          accepted_at = null;
        }

        if (assignMode === "manual") {
          driver_id = selectedDriverId;

          if (!initialData?.driver_id || initialData.driver_id !== selectedDriverId) {
            status = "accepted";
            accepted_at = initialData?.accepted_at || new Date().toISOString();
          }
        }

        if (assignMode === "auto") {
          driver_id = null;
          status = "requested";
          accepted_at = null;
        }
      }

      const { data: isInsideZone, error: zoneError } = await supabase.rpc(
        "check_if_point_in_zone",
        {
          lng: formData.depart_lng,
          lat: formData.depart_lat,
        }
      );

      if (zoneError) {
        alert("Impossible de vérifier les zones.");
        setIsSubmitting(false);
        return;
      }

      if (!isInsideZone) {
        const confirm = window.confirm(
          "⚠️ Cette adresse est hors zone.\n\nSouhaitez-vous quand même créer la course ?"
        );

        if (!confirm) {
          setIsSubmitting(false);
          return;
        }
      }

      const payload = {
        client_id: formData.client_id,
        pickup_address: formData.depart,
        dropoff_address: formData.destination,
        pickup_lat: formData.depart_lat,
        pickup_lng: formData.depart_lng,
        dropoff_lat: formData.destination_lat,
        dropoff_lng: formData.destination_lng,
        extra_info: formData.extra_info,
        status,
        driver_id,
        accepted_at,
        meta: {
          nom: formData.nom,
          telephone: formData.telephone,
          email: formData.email,
          type_reservation: formData.type_reservation,
        },
        date_heure:
          formData.type_reservation === "planifiee"
            ? new Date(dateHeure).toISOString()
            : null,
      };

      const query =
        mode === "edit"
          ? supabase.from("rides").update(payload).eq("id", rideId)
          : supabase.from("rides").insert([payload]);

      const { error } = await query;
      if (error) throw error;

      alert(
        mode === "edit"
          ? "Course modifiée avec succès."
          : "Course créée avec succès."
      );

      sessionStorage.removeItem(draftKey);
      navigate("/rides");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l’enregistrement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="card" onSubmit={handleSubmit}>
      <div className="form-row">
        <label>Recherche client (téléphone ou email)</label>
        <div className="inline">
          <input
            disabled={isLocked}
            type="text"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="ex: 5145551234 ou email"
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={handleSearchClient}
            disabled={searchLoading}
          >
            {searchLoading ? "Recherche..." : "Rechercher"}
          </button>
        </div>
      </div>

      <hr className="separator" />

      <h2>Formulaire créer une course</h2>

      <div className="form-row">
        <label>Nom complet</label>
        <input
          disabled={isLocked}
          type="text"
          value={formData.nom}
          onChange={(e) => handleChange("nom", e.target.value)}
        />
      </div>

      <div className="form-row">
        <label>Téléphone</label>
        <input
          disabled={isLocked}
          type="text"
          value={formData.telephone}
          onChange={(e) => handleChange("telephone", e.target.value)}
        />
      </div>

      <div className="form-row">
        <label>Email</label>
        <input
          disabled={isLocked}
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
      </div>

      <div className="form-row">
        <label>Adresse de prise en charge</label>
        <AddressAutocompletePlaces
          value={formData.depart}
          placeholder="Adresse de prise en charge"
          onSelect={({ address, lat, lng }) =>
            setFormData((prev) => ({
              ...prev,
              depart: address,
              depart_lat: lat,
              depart_lng: lng,
            }))
          }
        />
      </div>

      <div className="form-row">
        <label>Adresse de destination</label>
        <AddressAutocompletePlaces
          value={formData.destination}
          placeholder="Adresse de destination"
          onSelect={({ address, lat, lng }) =>
            setFormData((prev) => ({
              ...prev,
              destination: address,
              destination_lat: lat,
              destination_lng: lng,
            }))
          }
        />
      </div>

      <div className="form-row">
        <label>Type de réservation</label>
        <select
          value={formData.type_reservation}
          onChange={(e) => handleChange("type_reservation", e.target.value)}
        >
          <option value="immediate">Immédiate</option>
          <option value="planifiee">Planifiée</option>
        </select>
      </div>

      {formData.type_reservation === "planifiee" && (
        <div className="form-row">
          <label>Date et heure de prise en charge</label>
          <input
            type="datetime-local"
            value={dateHeure}
            min={formatDateTimeLocal(new Date())}
            onChange={(e) => setDateHeure(e.target.value)}
            required
          />
        </div>
      )}

      <div className="form-row">
        <label>Informations supplémentaires</label>
        <textarea
          rows={4}
          value={formData.extra_info}
          onChange={(e) => handleChange("extra_info", e.target.value)}
          placeholder="Notes pour le chauffeur..."
        />
      </div>

      <div className="form-row">
        <label>Mode d'assignation</label>
        <div className="inline">
          <label>
            <input
              type="radio"
              value="auto"
              checked={assignMode === "auto"}
              onChange={() => handleAssignModeChange("auto")}
            />
            Auto
          </label>
          <label>
            <input
              type="radio"
              value="manual"
              checked={assignMode === "manual"}
              onChange={() => handleAssignModeChange("manual")}
            />
            Manuel (choisir un chauffeur)
          </label>
          <label>
            <input
              type="radio"
              value="unassigned"
              checked={assignMode === "unassigned"}
              onChange={() => handleAssignModeChange("unassigned")}
            />
            Non assigné
          </label>
        </div>
      </div>

      {assignMode === "manual" && (
        <div className="form-row">
          <label>Chauffeur</label>
          <select
            value={selectedDriverId || ""}
            onChange={(e) => setSelectedDriverId(e.target.value)}
          >
            <option value="">-- Sélectionner un chauffeur --</option>
            {driversOnline.map((d) => (
              <option key={d.id} value={d.id}>
                {d.num_driver} ({d.status})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          className="btn-primary"
          disabled={isSubmitting || isLocked}
        >
          {isSubmitting
            ? mode === "edit"
              ? "Modification..."
              : "Création..."
            : mode === "edit"
            ? "Modifier la course"
            : "Créer la course"}
        </button>
      </div>
    </form>
  );
};

export default RideForm;


